/**
 * CS: Cart Drawer Product Recommendations.
 * Web Component — morph-safe with static product cache.
 * Fetches Shopify recommendations, renders cards with size selects + ADD buttons.
 */

/* ── Module-level helpers (shared across instances) ── */

function formatMoney(cents) {
  var d = cents / 100;
  var formatted = d % 1 === 0 ? '$' + d + '.00' : '$' + d.toFixed(2);
  var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
  if (active && active !== 'USD') {
    formatted += ' ' + active;
  }
  return formatted;
}

function escapeHtml(str) {
  var div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function getSizeImageUrl(url, width) {
  if (!url) return '';
  var separator = url.indexOf('?') !== -1 ? '&' : '?';
  return url + separator + 'width=' + width;
}

var _riseBadgeConfig = null;
function getRiseBadgeConfig() {
  if (_riseBadgeConfig) return _riseBadgeConfig;
  var el = document.getElementById('cs-rise-badge-config');
  if (!el) return null;
  try { _riseBadgeConfig = JSON.parse(el.textContent); return _riseBadgeConfig; } catch (e) { return null; }
}

function getRiseBadgeHtml(productType) {
  var config = getRiseBadgeConfig();
  if (!config) return '';
  var pt = (productType || '').toLowerCase();
  if (config.high && config.high.types.indexOf(pt) !== -1) {
    return '<span class="cs-rise-badge cs-rise-badge--high">' + escapeHtml(config.high.label) + '</span>';
  }
  if (config.mid && config.mid.types.indexOf(pt) !== -1) {
    return '<span class="cs-rise-badge cs-rise-badge--mid">' + escapeHtml(config.mid.label) + '</span>';
  }
  return '';
}

function buildSizeOptions(product) {
  var sizes = [];
  var seenSizes = {};
  product.variants.forEach(function (v) {
    var sizeName = v.option1 || v.title;
    if (!seenSizes[sizeName] && v.available) {
      seenSizes[sizeName] = true;
      sizes.push({ name: sizeName, id: v.id });
    }
  });
  return sizes;
}

/* ── Web Component ── */

class CsCartRecs extends HTMLElement {
  /* Static cache survives morph disconnect/reconnect cycles */
  static cachedProducts = null;
  static fetching = false;

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.lastCartIds = '';
    this.morphTimer = null;

    /* Event delegation for card interactions */
    this.addEventListener('click', this.handleClick.bind(this), { signal });
    this.addEventListener('change', this.handleChange.bind(this), { signal });

    /* Listen for cart updates */
    document.addEventListener('cart:update', this.refresh, { signal });

    /* MutationObserver on dialog for morph detection + drawer open */
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog) {
      this.observer = new MutationObserver((muts) => {
        if (muts.some((m) => m.type === 'attributes' && m.attributeName === 'open') && dialog.hasAttribute('open')) {
          this.refresh();
          return;
        }
        clearTimeout(this.morphTimer);
        this.morphTimer = setTimeout(() => this.populateRecs(), 300);
      });
      this.observer.observe(dialog, { attributes: true, childList: true, subtree: true });
    }

    /* Initial populate */
    this.refresh();
  }

  refresh = () => {
    setTimeout(() => this.populateRecs(), 50);
  };

  getCartProductIds() {
    var raw = this.dataset.cartProductIds || '';
    if (!raw) return [];
    return raw.split(',').map(function (id) { return parseInt(id); });
  }

  renderProducts(products) {
    var cartIds = this.getCartProductIds();
    var displayLimit = parseInt(this.dataset.limit) || 6;
    var html = '';
    var count = 0;

    for (var i = 0; i < products.length; i++) {
      if (count >= displayLimit) break;
      var product = products[i];

      if (!product.variants || !product.variants.length) continue;
      var firstVariant = product.variants[0];
      if (typeof firstVariant.price !== 'number') continue;

      if (product.type === 'Warehouse') continue;
      if (cartIds.indexOf(product.id) !== -1) continue;

      var image = product.featured_image || (product.images && product.images[0]);
      var imageSrc = getSizeImageUrl(image, 300);

      var priceHtml = '';
      if (firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price) {
        priceHtml = '<s>' + formatMoney(firstVariant.compare_at_price) + '</s> '
          + '<span class="sale-price">' + formatMoney(firstVariant.price) + '</span>';
      } else {
        priceHtml = formatMoney(firstVariant.price);
      }

      var sizes = buildSizeOptions(product);
      var sizeHtml = '';
      if (sizes.length > 1) {
        sizeHtml = '<select class="cs-cart-recs__size-select" aria-label="Size">';
        sizeHtml += '<option value="" disabled selected>Size</option>';
        sizes.forEach(function (s) {
          sizeHtml += '<option value="' + s.id + '">' + escapeHtml(s.name) + '</option>';
        });
        sizeHtml += '</select>';
      } else if (sizes.length === 1) {
        sizeHtml = '<select class="cs-cart-recs__size-select" aria-label="Size">'
          + '<option value="' + sizes[0].id + '" selected>' + escapeHtml(sizes[0].name) + '</option>'
          + '</select>';
      }

      var defaultVariantId = sizes.length <= 1 ? firstVariant.id : '';

      html += '<div class="cs-cart-recs__card">'
        + '<div class="cs-cart-recs__card-image">'
        + (imageSrc ? '<img src="' + imageSrc + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="70" height="85">' : '')
        + '</div>'
        + '<div class="cs-cart-recs__card-info">'
        + '<div class="cs-cart-recs__card-title"><a href="' + product.url + '">' + escapeHtml(product.title) + '</a></div>'
        + getRiseBadgeHtml(product.type)
        + '<div class="cs-cart-recs__card-price">' + priceHtml + '</div>'
        + '<div class="cs-cart-recs__card-actions">'
        + sizeHtml
        + '<button class="cs-cart-recs__add-btn" data-variant-id="' + defaultVariantId + '">ADD</button>'
        + '</div>'
        + '</div>'
        + '</div>';

      count++;
    }

    return html;
  }

  populateRecs() {
    const container = this.querySelector('[data-cs-recs-container]');
    if (!container) return;

    var currentCartIds = this.getAttribute('data-cart-product-ids') || '';
    if (container.children.length > 0) {
      if (currentCartIds === this.lastCartIds) return;
      container.innerHTML = '';
    }
    this.lastCartIds = currentCartIds;

    if (CsCartRecs.cachedProducts) {
      var html = this.renderProducts(CsCartRecs.cachedProducts);
      if (!html) {
        this.style.display = 'none';
        return;
      }
      container.innerHTML = html;
      return;
    }

    if (CsCartRecs.fetching) return;
    CsCartRecs.fetching = true;

    var productId = this.dataset.productId;
    var fetchLimit = parseInt(this.dataset.fetchLimit) || parseInt(this.dataset.limit) || 6;

    if (!productId) {
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          CsCartRecs.fetching = false;
          if (cart.items && cart.items.length > 0) {
            this.dataset.productId = cart.items[0].product_id;
            this.populateRecs();
          }
        })
        .catch(() => { CsCartRecs.fetching = false; });
      return;
    }

    var url = '/recommendations/products.json?product_id=' + productId + '&limit=' + fetchLimit + '&intent=related';

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        CsCartRecs.fetching = false;

        if (!data.products || data.products.length === 0) {
          this.style.display = 'none';
          return;
        }

        CsCartRecs.cachedProducts = data.products;

        var html = this.renderProducts(CsCartRecs.cachedProducts);
        if (!html) {
          this.style.display = 'none';
          return;
        }
        const freshContainer = this.querySelector('[data-cs-recs-container]');
        if (freshContainer) {
          freshContainer.innerHTML = html;
        }
      })
      .catch((err) => {
        CsCartRecs.fetching = false;
        console.warn('CS recs: fetch failed', err && err.message || '');
        this.style.display = 'none';
      });
  }

  handleChange(e) {
    const select = e.target.closest('.cs-cart-recs__size-select');
    if (!select) return;
    const card = select.closest('.cs-cart-recs__card');
    const addBtn = card ? card.querySelector('.cs-cart-recs__add-btn') : null;
    if (addBtn) addBtn.dataset.variantId = select.value;
  }

  handleClick(e) {
    const btn = e.target.closest('.cs-cart-recs__add-btn');
    if (!btn) return;

    e.preventDefault();
    const card = btn.closest('.cs-cart-recs__card');
    const select = card ? card.querySelector('.cs-cart-recs__size-select') : null;

    if (select && !select.value) {
      select.classList.add('cs-cart-recs__size-select--error');
      select.addEventListener('change', function () {
        this.classList.remove('cs-cart-recs__size-select--error');
      }, { once: true });
      return;
    }

    const vid = parseInt(btn.dataset.variantId);
    if (!vid) return;

    if (!btn.dataset.lockedWidth) {
      btn.style.width = btn.offsetWidth + 'px';
      btn.dataset.lockedWidth = '1';
    }

    btn.disabled = true;
    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.textContent = 'ERROR';
          setTimeout(() => { btn.textContent = 'ADD'; }, 2000);
          return;
        }

        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.textContent = '\u2713';
        setTimeout(() => { btn.textContent = 'ADD'; }, 1500);

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'cs-cart-recs',
            data: { source: 'product-form-component', itemCount: 1 },
          },
        }));

        if (window.innerWidth <= 749) {
          setTimeout(() => {
            const scrollEl = document.querySelector('.cart-drawer__content');
            if (scrollEl) scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
          }, 600);
        }
      })
      .catch(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.textContent = 'ADD';
      });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.morphTimer);
  }
}
customElements.define('cs-cart-recs', CsCartRecs);
